import { ReactNode, useContext, useMemo } from 'react';
import {
  Box,
  BoxProps,
  Button,
  Card,
  CardProps,
  Icon,
  List,
  ListItem,
  OverflowMenu,
  OverflowMenuItem,
  Text,
  TypeIconName,
  useDisclosure,
  useResponsive,
  useToast,
} from '@bitrise/bitkit';
import { parseISO } from 'date-fns';
import { numericToWeekdayIndexes } from '@/pages/App/components/StartBuildDialog/utils/daysToNumeric';
import useStartBuild from '@/hooks/useStartBuild';
import { GlobalPropsContext } from '@/components/page/GlobalPropsContext';
import { GlobalPropsWithAccount } from '@/models/GlobalProps';
import useInitialState from '@/hooks/useInitialState';
import useUpdateScheduledBuild from '@/pages/App/AppDetails/components/ScheduledBuilds/hooks/useUpdateScheduledBuild';
import AppForAppPage from '@/pages/App/types/AppForAppPage';
import BuildScheduleDateTime from '../BuildScheduleTime/BuildScheduleDateTime';
import { triggerNowPath } from '../../../../../utils/monolithApiRouteService';
import { queryClient } from '../../../../../utils/query';
import { ScheduledBuild } from './scheduledBuilds.types';
import parseBuildParams from './utils/parseBuildParams';
import DeleteScheduledBuildDialog from './DeleteScheduledBuildDialog';

interface MetaProps extends BoxProps {
  iconName: TypeIconName;
  text?: string;
}

const Meta = ({ children, iconName, text, ...rest }: MetaProps) => (
  <Box display="flex" alignItems="center" gap="4" paddingInline="12" paddingBlock="8" color="neutral.40" {...rest}>
    <Icon name={iconName} size="16" />
    {!!text && (
      <Text as="span" size="2">
        {text}
      </Text>
    )}
    {children}
  </Box>
);

export interface ScheduledBuildItemProps extends CardProps {
  app: AppForAppPage;
  scheduledBuildItemData: ScheduledBuild;
  onUpdateScheduledBuild: (scheduledBuild: ScheduledBuild) => void;
  lastErrorMessage?: string;
}

const ScheduledBuildItem = ({
  app,
  scheduledBuildItemData,
  onUpdateScheduledBuild,
  lastErrorMessage,
  ...rest
}: ScheduledBuildItemProps) => {
  const { user } = useContext(GlobalPropsContext) as GlobalPropsWithAccount;
  const toast = useToast();
  const { isMobile } = useResponsive();

  const {
    id,
    buildParams,
    isEnabled: initialIsEnabled,
    nextRun,
    repetitionEnabled,
    repeatEvery,
    repeatInterval,
    startDate,
    monthlyRepetition,
    yearlyRepetition,
    setupLocation,
    weekdays,
    cronExpression,
  } = scheduledBuildItemData;

  const parsedBuildParams = useMemo(() => parseBuildParams(buildParams), [buildParams]);

  if (!parsedBuildParams.hookInfo) {
    parsedBuildParams.hookInfo = {
      buildTriggerToken: '',
      type: 'bitrise',
    };
  }
  parsedBuildParams.hookInfo.buildTriggerToken = app.buildTriggerToken;
  if (parsedBuildParams.hookInfo.apiToken) {
    delete parsedBuildParams.hookInfo.apiToken;
  }

  const [isEnabled, setIsEnabled] = useInitialState(initialIsEnabled);

  const { onClose: onDeleteDialogClose, onOpen: onDeleteDialogOpen, isOpen: isDeleteDialogOpen } = useDisclosure();

  let cardStatusColor: string;
  if (lastErrorMessage) {
    cardStatusColor = 'border/error';
  } else if (isEnabled) {
    cardStatusColor = 'sys/info/base';
  } else {
    cardStatusColor = 'sys/neutral/strong';
  }

  let iconName: TypeIconName;
  if (lastErrorMessage) {
    iconName = 'ErrorCircleFilled';
  } else if (isEnabled) {
    iconName = 'Calendar';
  } else {
    iconName = 'Pause';
  }

  let metaColor: string;
  if (lastErrorMessage) {
    metaColor = 'status/critical/bg';
  } else if (isEnabled) {
    metaColor = 'sys/info/minimal';
  } else {
    metaColor = 'sys/neutral/minimal';
  }

  const style = {
    card: {
      position: 'relative',
      paddingInlineStart: '7px',
      _before: {
        content: `""`,
        position: 'absolute',
        top: 0,
        bottom: 0,
        left: 0,
        width: '7px',
        backgroundColor: cardStatusColor,
        borderTopLeftRadius: '8',
        borderBottomLeftRadius: '8',
      },
    } as BoxProps,
    up: {
      display: 'flex',
      alignItems: 'center',
      flexGrow: '1',
      gap: '12',
      padding: '12',
      maxWidth: '100%',
      overflow: 'hidden',
    } as BoxProps,
  };

  const { mutate: startBuild } = useStartBuild(
    {
      appSlug: app.slug,
      buildTriggerToken: app.buildTriggerToken,
      path: triggerNowPath(id),
    },
    {
      onSuccess: ({ data }) => {
        if (data.results?.length === 1 && data.results[0].buildUrl) {
          window.location.href = data.results[0].buildUrl;
        }
      },
      onError: (error) => {
        let errorTitle = 'Failed to start build';
        let errorDescription: ReactNode = 'Unknown error';
        if (error.response?.data.errorMsg) {
          errorDescription = error.response?.data.errorMsg;
        } else if (error.response?.data.results?.length) {
          const resultErrors: string[] = [];
          error.response?.data.results.forEach((result) => {
            if (result.status === 'error') {
              resultErrors.push(`${result.triggeredPipeline || result.triggeredWorkflow}: ${result.message}`);
            }
          });
          if (resultErrors.length > 1) {
            errorTitle += 's';
          }
          errorDescription = (
            <>
              {resultErrors.map((err) => (
                <List>
                  <ListItem>{err}</ListItem>
                </List>
              ))}
            </>
          );
        } else {
          errorDescription = error.response?.data.message || 'Unknown error';
        }
        toast({
          title: errorTitle,
          description: errorDescription,
          status: 'error',
          isClosable: true,
        });
        queryClient.refetchQueries({ queryKey: ['scheduledBuilds', app.slug] });
      },
      requestConfig: {
        params: { build_trigger_token: app.buildTriggerToken },
      },
    },
  );

  const { mutate: updateScheduledBuild } = useUpdateScheduledBuild({
    app,
  });

  const updateScheduledBuildIsEnable = (updatedValue: boolean) => {
    setIsEnabled(updatedValue);

    const updatedSchedule: any = {
      ...scheduledBuildItemData,
      buildParams: parsedBuildParams,
      isEnabled: updatedValue,
    };

    if (
      Number.isInteger(scheduledBuildItemData.hourAtSetupLocation) &&
      Number.isInteger(scheduledBuildItemData.minuteAtSetupLocation)
    ) {
      updatedSchedule.time = `${scheduledBuildItemData.hourAtSetupLocation.toString().padStart(2, '0')}:${scheduledBuildItemData.minuteAtSetupLocation.toString().padStart(2, '0')}`;
    }

    updateScheduledBuild({
      schedule: updatedSchedule,
    });
  };

  return (
    <Card
      role="group"
      sx={{
        ...style.card,
      }}
      {...rest}
    >
      <Box
        display="flex"
        flexDirection={['column', 'row']}
        alignItems={['flex-start', 'center']}
        borderBottom="1px solid"
        borderBottomColor="neutral.93"
      >
        <Box __css={style.up}>
          <Icon name={iconName} size="24" color={cardStatusColor} />
          {isMobile ? (
            <BuildScheduleDateTime
              nextRun={parseISO(nextRun)}
              repetitionEnabled={repetitionEnabled}
              repeatEvery={repeatEvery}
              repeatInterval={repeatInterval}
              startDate={startDate}
              monthlyRepetition={monthlyRepetition}
              yearlyRepetition={yearlyRepetition}
              setupLocation={setupLocation}
              weekdayIndexes={numericToWeekdayIndexes(weekdays)}
              cronExpression={cronExpression}
              size="2"
              whiteSpace="normal"
            />
          ) : (
            <Box display="flex" flexDir="column" textOverflow="ellipsis" whiteSpace="nowrap" overflow="hidden">
              <Text as="span" hasEllipsis size="2">
                {parsedBuildParams.buildParams.commitMessage || 'No commit message.'}
              </Text>
              {lastErrorMessage && (
                <Text hasEllipsis textStyle="body/md/regular" color="status/critical/text">
                  {lastErrorMessage}
                </Text>
              )}
            </Box>
          )}
        </Box>
        {isMobile && (
          <Box __css={style.up} borderTop="1px solid" borderTopColor="neutral.93">
            <Text as="span" hasEllipsis maxWidth="720px" size="2">
              {parsedBuildParams.buildParams.commitMessage || 'No commit message.'}
            </Text>
          </Box>
        )}
        {!isMobile && (
          <Box
            display="flex"
            flexDirection="row"
            justifyContent="flex-end"
            alignItems="center"
            gap="16"
            marginRight={12}
          >
            <Button
              variant="tertiary"
              size="sm"
              leftIconName="Play"
              color="sys/interactive/base"
              onClick={() =>
                startBuild({ buildParams: parsedBuildParams.buildParams, triggeredBy: `manual-${user.username}` })
              }
            >
              Trigger now
            </Button>
            <OverflowMenu buttonSize="sm" triggerLabel="More actions" withPortal>
              <OverflowMenuItem iconName="Pencil" onClick={() => onUpdateScheduledBuild(scheduledBuildItemData)}>
                Edit configuration
              </OverflowMenuItem>
              <OverflowMenuItem
                iconName={isEnabled ? 'Pause' : 'Play'}
                onClick={() => updateScheduledBuildIsEnable(!isEnabled)}
              >
                {isEnabled ? 'Pause' : 'Start'} schedule
              </OverflowMenuItem>
              <OverflowMenuItem iconName="Trash" isDanger onClick={onDeleteDialogOpen}>
                Delete
              </OverflowMenuItem>
            </OverflowMenu>
            <DeleteScheduledBuildDialog
              isOpen={isDeleteDialogOpen}
              onClose={onDeleteDialogClose}
              scheduledBuildItemId={scheduledBuildItemData.id}
              app={app}
            />
          </Box>
        )}
      </Box>
      <Box display="flex" justifyContent={['center', 'flex-end']} flexDirection={['column', 'row']}>
        <Meta
          iconName="Branch"
          text={parsedBuildParams.buildParams.branch}
          backgroundColor={metaColor}
          color={cardStatusColor}
          maxWidth="100%"
        />
        {(parsedBuildParams.buildParams.workflowId || parsedBuildParams.buildParams.pipelineId) && (
          <Meta
            iconName={parsedBuildParams.buildParams.workflowId ? 'Workflow' : 'WorkflowFlow'}
            text={parsedBuildParams.buildParams.workflowId || parsedBuildParams.buildParams.pipelineId}
            maxWidth="100%"
            borderTop={['1px solid', 'none']}
            borderTopColor="neutral.93"
          />
        )}
        {!isMobile && (
          <Meta iconName="Clock" marginInlineStart="auto" maxWidth="100%">
            <BuildScheduleDateTime
              nextRun={parseISO(nextRun)}
              repetitionEnabled={repetitionEnabled}
              repeatEvery={repeatEvery}
              repeatInterval={repeatInterval}
              startDate={startDate}
              monthlyRepetition={monthlyRepetition}
              yearlyRepetition={yearlyRepetition}
              setupLocation={setupLocation}
              weekdayIndexes={numericToWeekdayIndexes(weekdays)}
              cronExpression={cronExpression}
              size="2"
            />
          </Meta>
        )}
      </Box>
      {isMobile && (
        <Box
          display="flex"
          flexDirection="row"
          justifyContent="space-between"
          alignItems="center"
          gap="16"
          paddingRight={12}
          width="100%"
          borderTop="1px solid"
          borderTopColor="neutral.93"
        >
          <Button
            variant="tertiary"
            size="lg"
            leftIconName="Play"
            color="sys/interactive/base"
            onClick={() =>
              startBuild({ buildParams: parsedBuildParams.buildParams, triggeredBy: `manual-${user.username}` })
            }
          >
            Trigger now
          </Button>
          <OverflowMenu buttonSize="sm" triggerLabel="More actions" withPortal>
            <OverflowMenuItem iconName="Pencil" onClick={() => onUpdateScheduledBuild(scheduledBuildItemData)}>
              Edit configuration
            </OverflowMenuItem>
            <OverflowMenuItem
              iconName={isEnabled ? 'Pause' : 'Play'}
              onClick={() => updateScheduledBuildIsEnable(!isEnabled)}
            >
              {isEnabled ? 'Pause' : 'Start'} schedule
            </OverflowMenuItem>
            <OverflowMenuItem iconName="Trash" isDanger onClick={onDeleteDialogOpen}>
              Delete
            </OverflowMenuItem>
          </OverflowMenu>
          <DeleteScheduledBuildDialog
            isOpen={isDeleteDialogOpen}
            onClose={onDeleteDialogClose}
            scheduledBuildItemId={scheduledBuildItemData.id}
            app={app}
          />
        </Box>
      )}
    </Card>
  );
};

export default ScheduledBuildItem;
